/**
 * The project-display-name join.
 *
 * A Forge workspace and the CMMD project it plans against are two different
 * records. The workspace is named after its checkout — for project 198 that is
 * `forge-1-0-cmmd-builds-cmmd`, the repository slug generated from the board
 * title at import — so a sidebar row showing the workspace name shows a
 * slugified board title no matter what the project is called in Forge.
 *
 * The link record is what closes that gap: it maps
 * `(forgeEnvironmentId, forgeProjectId)` to a CMMD project id, and the CMMD
 * record carries the display name. Same join the project ICON already uses, and
 * for the same reason — surfaces hold ids and little else, so rebuilding this
 * per surface is how half of them end up honouring the name and half do not.
 *
 * Only `shortName` is answered here. A workspace with no linked project, or one
 * whose project has no display name, returns undefined so the caller keeps its
 * own label rather than being handed a board title it did not ask for.
 */

import type { ProjectLinkRow } from "~/cmmd/projectLinkListResource";

/** The CMMD fields this join needs: an id and the display name, if one is set. */
export interface CmmdProjectDisplayNameRow {
  readonly id: string;
  readonly shortName?: string | null;
}

export type ProjectDisplayNameByProjectKey = ReadonlyMap<string, string>;

function projectLinkKey(environmentId: string, projectId: string): string {
  return `${environmentId}:${projectId}`;
}

/**
 * Build `(environmentId, projectId)` → the project's Forge display name.
 *
 * A blank name is treated as unset, matching `projectDisplayName()` — otherwise
 * a row stored before CMMD normalised blanks would render as an empty label.
 */
export function buildProjectDisplayNameByProjectKey(
  links: readonly ProjectLinkRow[],
  cmmdProjects: readonly CmmdProjectDisplayNameRow[],
): ProjectDisplayNameByProjectKey {
  const nameByCmmdProjectId = new Map<string, string>();
  for (const project of cmmdProjects) {
    const shortName = project.shortName?.trim();
    if (shortName === undefined || shortName.length === 0) continue;
    nameByCmmdProjectId.set(String(project.id), shortName);
  }

  const nameByProjectKey = new Map<string, string>();
  for (const link of links) {
    const name = nameByCmmdProjectId.get(String(link.cmmdProjectId));
    if (name === undefined) continue;
    nameByProjectKey.set(projectLinkKey(link.forgeEnvironmentId, link.forgeProjectId), name);
  }
  return nameByProjectKey;
}

/**
 * The Forge display name for a workspace, or `undefined` to keep its own label.
 *
 * `undefined` rather than the board title on purpose: the caller already has a
 * name it can show, and replacing it with `title` here would undo the very
 * split this exists to express.
 */
export function resolveProjectDisplayName(
  environmentId: string | null | undefined,
  projectId: string | null | undefined,
  nameByProjectKey: ProjectDisplayNameByProjectKey,
): string | undefined {
  if (!environmentId || !projectId) return undefined;
  return nameByProjectKey.get(projectLinkKey(environmentId, projectId));
}
